import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Heart, MessageCircle, Bookmark, Volume2, VolumeX, Film } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/site";
import { stageApi, compact } from "@/lib/stageApi";
import { reelsApi } from "@/lib/stageExtraApi";
import { CreatorAvatar, VerifiedMark } from "@/components/stage/PostCard";

const isVideo = (p) => p.kind === "video" || (p.media || []).some((m) => m && m.type === "video");
const videoOf = (p) => (p.media || []).find((m) => m && m.type === "video");

const REEL_TABS = [
  { key: "foryou", label: "For you" },
  { key: "following", label: "Following", auth: true },
  { key: "friends", label: "Friends", auth: true },
  { key: "trending", label: "Trending" },
  { key: "nearby", label: "Nearby" },
];
const REEL_EMPTY = {
  foryou: ["No Reels yet", "When creators post video, it plays here. Post yours from the Fable + app.", "/get-the-app", "Get the app"],
  following: ["Nothing from people you follow yet", "Follow creators and their videos land here.", "/creators", "Discover creators"],
  friends: ["No videos from friends yet", "Friends are people you follow who follow you back.", "/creators", "Find people"],
  trending: ["Nothing trending this week", "The fastest-rising videos of the last 7 days show up here.", "/stage/feed", "Open Pulse"],
  nearby: ["Nothing nearby yet", "Be the first creator posting from your area.", "/get-the-app", "Get the app"],
};

function Reel({ post, muted, onToggleMute }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const ref = useRef(null);
  const [land, setLand] = useState(false);
  const [liked, setLiked] = useState(!!post.viewer?.liked);
  const [likes, setLikes] = useState(post.stats?.likes || 0);
  const [saved, setSaved] = useState(!!post.viewer?.saved);
  const v = videoOf(post);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([en]) => {
      if (en.isIntersecting && en.intersectionRatio >= 0.6) el.play().catch(() => null);
      else el.pause();
    }, { threshold: [0, 0.6, 1] });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const needLogin = () => { toast("Sign in to react."); navigate("/login", { state: { from: "/stage/reels" } }); };
  const like = async () => {
    if (!user) return needLogin();
    const next = !liked; setLiked(next); setLikes((n) => Math.max(0, n + (next ? 1 : -1)));
    try { await stageApi.like(post.id, next); } catch (e) { setLiked(!next); setLikes((n) => Math.max(0, n + (next ? -1 : 1))); toast.error(e.message); }
  };
  const save = async () => {
    if (!user) return needLogin();
    const next = !saved; setSaved(next);
    try { await stageApi.save(post.id, next); } catch (e) { setSaved(!next); toast.error(e.message); }
  };
  const a = post.author || {};

  return (
    <article className="relative mx-auto h-[calc(100vh-4rem)] w-full max-w-[480px] snap-start overflow-hidden bg-black sm:my-4 sm:h-[85vh] sm:rounded-3xl" data-testid={`reel-${post.id}`}>
      <video ref={ref} src={v?.url} poster={v?.poster || undefined} muted={muted} loop playsInline preload="metadata" className={"h-full w-full " + (land ? "object-contain" : "object-cover")} onLoadedMetadata={(e) => setLand(e.currentTarget.videoWidth > e.currentTarget.videoHeight * 1.1)} onClick={(e) => (e.currentTarget.paused ? e.currentTarget.play() : e.currentTarget.pause())} />
      <button type="button" onClick={onToggleMute} aria-label={muted ? "Unmute" : "Mute"} className="absolute right-3 top-3 rounded-full bg-black/50 p-2 text-white">{muted ? <VolumeX size={16} /> : <Volume2 size={16} />}</button>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4 pr-16 text-white">
        <Link to={`/@${a.handle}`} className="pointer-events-auto inline-flex items-center gap-2 font-semibold"><CreatorAvatar author={a} size={32} /> {a.display_name || a.handle} <VerifiedMark author={a} size={14} /></Link>
        {post.text && <p className="mt-2 line-clamp-3 text-sm">{post.text}</p>}
        {post.sound && <p className="mt-1 text-xs text-white/80">♪ {post.sound.title || post.sound.name}</p>}
      </div>
      <div className="absolute bottom-6 right-3 flex flex-col items-center gap-4 text-white">
        <button type="button" onClick={like} aria-pressed={liked} aria-label="Like" className="flex flex-col items-center text-xs" data-testid={`reel-like-${post.id}`}><Heart size={26} fill={liked ? "currentColor" : "none"} className={liked ? "text-[var(--fable)]" : ""} /><span className="font-mono-num">{compact(likes)}</span></button>
        <Link to={`/stage/post/${post.id}`} aria-label="Comments" className="flex flex-col items-center text-xs"><MessageCircle size={26} /><span className="font-mono-num">{compact(post.stats?.comments)}</span></Link>
        <button type="button" onClick={save} aria-pressed={saved} aria-label="Save" className="flex flex-col items-center text-xs"><Bookmark size={26} fill={saved ? "currentColor" : "none"} /></button>
      </div>
    </article>
  );
}

export default function Reels() {
  usePageMeta("Reels", "Full-screen video from creators on Fable + Stage: For you, Following, Friends, Trending and Nearby.");
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const want = params.get("tab") || "foryou";
  const tab = REEL_TABS.some((t) => t.key === want) && !(REEL_TABS.find((t) => t.key === want).auth && !user) ? want : "foryou";
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [meta, setMeta] = useState({});
  const [state, setState] = useState("loading");
  const [muted, setMuted] = useState(true);
  const busy = useRef(false);

  const load = useCallback(async (reset, cur) => {
    if (busy.current) return;
    busy.current = true;
    try {
      const d = await reelsApi.feed(tab, reset ? null : cur);
      setItems((prev) => { if (reset) return d.items || []; const seen = new Set(prev.map((x) => x.id)); return [...prev, ...(d.items || []).filter((x) => !seen.has(x.id))]; });
      setCursor(d.next_cursor || null);
      setMeta({ needs_location: d.needs_location, place: d.place, scope: d.scope });
      setState("idle");
    } catch {
      setState((s) => (reset ? "error" : s));
    } finally {
      busy.current = false;
    }
  }, [tab]);

  useEffect(() => { setState("loading"); setItems([]); setCursor(null); load(true, null); }, [load, user?.id]);
  const pick = (k) => { const n = new URLSearchParams(params); if (k === "foryou") n.delete("tab"); else n.set("tab", k); setParams(n, { replace: true }); };
  const empty = REEL_EMPTY[tab] || REEL_EMPTY.foryou;

  return (
    <div className="bg-background">
      <div className="mx-auto max-w-[480px] px-4 pt-4 sm:px-0">
        <div className="flex items-center justify-between">
          <h1 className="font-serif-display text-2xl font-bold">Reels</h1>
          <Link to="/stage/feed" className="text-sm text-muted-foreground hover:underline">Pulse feed</Link>
        </div>
        <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none]" role="tablist" aria-label="Reels feeds">
          {REEL_TABS.filter((t) => !t.auth || user).map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => pick(t.key)} data-testid={"reels-tab-" + t.key}
              className={"whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors " + (tab === t.key ? "bg-[#E0007A] text-white" : "bg-muted text-muted-foreground hover:text-foreground")}>{t.label}</button>
          ))}
          {!user && <Link to="/login" state={{ from: "/stage/reels" }} className="whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium text-muted-foreground hover:underline">Sign in for Following and Friends</Link>}
        </div>
        {tab === "nearby" && meta.place && <p className="mt-1 text-xs text-muted-foreground">Creators in {meta.place}</p>}
      </div>
      {state === "loading" ? (
        <Skeleton className="mx-auto mt-4 h-[80vh] max-w-[480px] rounded-3xl" />
      ) : state === "error" ? (
        <p className="py-20 text-center text-muted-foreground">Couldn't load Reels right now.</p>
      ) : items.length === 0 ? (
        <div className="mx-auto mt-10 max-w-md rounded-2xl border border-dashed border-border p-10 text-center">
          <Film size={24} className="mx-auto text-muted-foreground" />
          <p className="mt-2 font-semibold">{tab === "nearby" && meta.needs_location ? "Add your city to see Nearby" : empty[0]}</p>
          <p className="mt-1 text-sm text-muted-foreground">{tab === "nearby" && meta.needs_location ? "Set your location on your profile in the app to see creators around you." : empty[1]}</p>
          <Link to={empty[2]}><Button className="mt-4 rounded-full">{empty[3]}</Button></Link>
        </div>
      ) : (
        <div className="snap-y snap-mandatory" data-testid="reels">
          {items.map((p) => <Reel key={p.id} post={p} muted={muted} onToggleMute={() => setMuted((m) => !m)} />)}
          {cursor && <div className="py-6 text-center"><Button variant="outline" className="rounded-full px-8" onClick={() => load(false, cursor)}>More</Button></div>}
        </div>
      )}
    </div>
  );
}
